import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMonthlyReport, getClients, getSubscriptions, getInvoices, getDeliverables,
  formatIQD, formatUSD
} from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

function Skeleton({ className = "" }) {
  return (
    <div className={`animate-pulse rounded-lg bg-gray-200 dark:bg-white/10 ${className}`} />
  );
}

function KPICard({ label, value, sub, icon, colorClass = 'text-primary', bgClass = 'bg-primary/10', loading }) {
  return (
    <div className="bg-white dark:bg-dark-deeper rounded-2xl p-5 border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">{label}</div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${bgClass} ${colorClass}`}>
          {icon}
        </div>
      </div>
      {loading ? (
        <div className="space-y-2 mt-2">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-4 w-1/3" />
        </div>
      ) : (
        <>
          <div className="text-2xl font-black text-dark dark:text-gray-100 leading-tight">{value ?? '—'}</div>
          {sub && <div className="text-xs text-gray-400 mt-1.5 font-medium">{sub}</div>}
        </>
      )}
    </div>
  );
}

function prevMonth(ym, n) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 - n, 1);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

function monthAr(ym) {
  const names = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
  const m = parseInt(ym.split('-')[1], 10);
  return names[m - 1] || ym;
}

const INV_CLASSES = {
  unpaid: 'bg-danger/10 text-danger border-danger/20',
  partial: 'bg-warning/10 text-warning border-warning/20',
  paid: 'bg-success/10 text-success border-success/20'
};
const INV_LABEL = { unpaid: 'غير مدفوع', partial: 'جزئي', paid: 'مدفوع' };

export default function DashboardPage() {
  const navigate = useNavigate();
  const curMonth = new Date().toISOString().substring(0, 7);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [clients, setClients] = useState([]);
  const [activeSubs, setActiveSubs] = useState([]);
  const [unpaidInv, setUnpaidInv] = useState([]);
  const [monthDelivs, setMonthDelivs] = useState(0);
  const [trend, setTrend] = useState([]);
  const [recentInv, setRecentInv] = useState([]);

  useEffect(() => {
    const m1 = prevMonth(curMonth, 1);
    const m2 = prevMonth(curMonth, 2);
    Promise.all([
      getMonthlyReport(curMonth).catch(() => null),
      getClients().catch(() => []),
      getSubscriptions({ status: 'active' }).catch(() => []),
      getInvoices({ status: 'unpaid' }).catch(() => []),
      getDeliverables().catch(() => []),
      getMonthlyReport(m1).catch(() => null),
      getMonthlyReport(m2).catch(() => null),
      getInvoices().catch(() => []),
    ]).then(([rep, cls, subs, unpaid, delivs, r1, r2, allInv]) => {
      setReport(rep);
      setClients(cls);
      setActiveSubs(subs);
      setUnpaidInv(unpaid);
      const delivArr = Array.isArray(delivs) ? delivs : [];
      setMonthDelivs(delivArr.filter(d => (d.delivery_date || d.delivered_at || '').startsWith(curMonth)).length);
      setRecentInv((allInv || []).slice(0, 5));
      setTrend([
        { month: monthAr(m2), إيرادات: r2?.revenue || 0, مصاريف: r2?.expenses?.total || 0 },
        { month: monthAr(m1), إيرادات: r1?.revenue || 0, مصاريف: r1?.expenses?.total || 0 },
        { month: monthAr(curMonth), إيرادات: rep?.revenue || 0, مصاريف: rep?.expenses?.total || 0 },
      ]);
    }).finally(() => setLoading(false));
  }, []);

  const margin = report?.margin || 0;
  const marginLabel = margin >= 40 ? 'ممتاز' : margin >= 20 ? 'جيد' : 'منخفض';
  const unpaidTotal = unpaidInv.reduce((s, i) => s + ((i.total_iqd || 0) - (i.paid_iqd || 0)), 0);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Title */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-dark dark:text-gray-100">لوحة التحكم</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">
            {new Date().toLocaleDateString('ar-IQ', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Outstanding alert */}
      {!loading && unpaidInv.length > 0 && (
        <div className="bg-danger/5 border border-danger/20 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-danger">
            <span className="text-xl">⚠️</span>
            <span className="font-bold text-sm">
              يوجد {unpaidInv.length} فاتورة غير مسددة بإجمالي {formatIQD(unpaidTotal)}
            </span>
          </div>
          <button
            onClick={() => navigate('/invoices')}
            className="text-sm font-bold text-primary hover:text-primary/80 transition-colors whitespace-nowrap self-start sm:self-auto"
          >
            عرض الفواتير ←
          </button>
        </div>
      )}

      {/* KPIs Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <KPICard label="إيرادات الشهر" loading={loading}
          value={formatIQD(report?.revenue)}
          sub={formatUSD((report?.revenue || 0) / 1500)}
          icon="💰" colorClass="text-success" bgClass="bg-success/10" />
        <KPICard label="مصاريف الشهر" loading={loading}
          value={formatIQD(report?.expenses?.total)}
          sub={formatUSD((report?.expenses?.total || 0) / 1500)}
          icon="💸" colorClass="text-danger" bgClass="bg-danger/10" />
        <KPICard label="صافي الربح" loading={loading}
          value={formatIQD(report?.profit)}
          sub={formatUSD((report?.profit || 0) / 1500)}
          icon="📈" colorClass={(report?.profit || 0) >= 0 ? 'text-success' : 'text-danger'} bgClass={(report?.profit || 0) >= 0 ? 'bg-success/10' : 'bg-danger/10'} />
        <KPICard label="هامش الربح" loading={loading}
          value={`${margin}%`} sub={marginLabel}
          icon="📊" colorClass={margin >= 40 ? 'text-success' : margin >= 20 ? 'text-warning' : 'text-danger'} bgClass={margin >= 40 ? 'bg-success/10' : margin >= 20 ? 'bg-warning/10' : 'bg-danger/10'} />
      </div>

      {/* Ops KPIs Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <KPICard label="إجمالي العملاء" loading={loading}
          value={clients.length} icon="👥" colorClass="text-purple-600" bgClass="bg-purple-100" />
        <KPICard label="اشتراكات نشطة" loading={loading}
          value={activeSubs.length} icon="📋" colorClass="text-primary" bgClass="bg-primary/10" />
        <KPICard label="فواتير غير مسددة" loading={loading}
          value={unpaidInv.length} sub={formatIQD(unpaidTotal)}
          icon="🧾" colorClass="text-danger" bgClass="bg-danger/10" />
        <KPICard label="تسليمات هذا الشهر" loading={loading}
          value={monthDelivs} icon="🎬" colorClass="text-warning" bgClass="bg-warning/10" />
      </div>

      {/* Middle Section: Recents & Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Invoices */}
        <div className="bg-white dark:bg-dark-deeper rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm overflow-hidden flex flex-col">
          <div className="p-5 border-b border-gray-50 dark:border-white/5 flex justify-between items-center bg-gray-50 dark:bg-white/5">
            <h3 className="font-bold text-dark dark:text-gray-100 flex items-center gap-2">
              <span>🧾</span> آخر الفواتير
            </h3>
            <button onClick={() => navigate('/invoices')} className="text-xs font-bold text-primary hover:text-primary/80 transition-colors">
              عرض الكل ←
            </button>
          </div>
          <div className="flex-1 p-2">
            {loading ? (
              <div className="p-3 space-y-4">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : recentInv.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">لا توجد فواتير</div>
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-white/5">
                {recentInv.map(inv => (
                  <div key={inv.id} className="p-3 flex justify-between items-center hover:bg-gray-50 dark:bg-transparent dark:hover:bg-white/5 rounded-xl transition-colors">
                    <div>
                      <div className="text-sm font-bold text-dark dark:text-gray-100">{inv.client_name}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{inv.invoice_number}</div>
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-black text-gray-700 dark:text-gray-200">{formatIQD(inv.total_iqd)}</div>
                      <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${INV_CLASSES[inv.payment_status] || 'bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-white/10'}`}>
                        {INV_LABEL[inv.payment_status] || '—'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Active Subscriptions */}
        <div className="bg-white dark:bg-dark-deeper rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm overflow-hidden flex flex-col">
          <div className="p-5 border-b border-gray-50 dark:border-white/5 flex justify-between items-center bg-gray-50 dark:bg-white/5">
            <h3 className="font-bold text-dark dark:text-gray-100 flex items-center gap-2">
              <span>📋</span> الاشتراكات النشطة
            </h3>
            <button onClick={() => navigate('/subscriptions')} className="text-xs font-bold text-primary hover:text-primary/80 transition-colors">
              عرض الكل ←
            </button>
          </div>
          <div className="flex-1 p-4 overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : activeSubs.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">لا توجد اشتراكات نشطة</div>
            ) : (
              <div className="space-y-3">
                {activeSubs.slice(0, 4).map(s => {
                  const usage = Math.min(s.usage_percent || 0, 100);
                  const usageColor = usage >= 80 ? 'bg-danger' : usage >= 50 ? 'bg-warning' : 'bg-success';
                  return (
                    <div key={s.id} className="bg-gray-50 dark:bg-white/5 rounded-xl p-3 border border-gray-100 dark:border-white/5 hover:bg-white dark:hover:bg-white/10 hover:shadow-sm hover:border-gray-200 transition-all">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="text-sm font-bold text-dark dark:text-gray-100">{s.client_name}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{s.package_name}</div>
                        </div>
                        <div className="text-xs font-medium text-gray-400 bg-white dark:bg-dark-deeper px-2 py-1 rounded-md border border-gray-100 dark:border-white/5">
                          ينتهي: {s.period_end}
                        </div>
                      </div>
                      <div className="h-1.5 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-500 ${usageColor}`} style={{ width: `${usage}%` }} />
                      </div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1.5 font-medium flex justify-between">
                        <span>{usage}% مستخدم</span>
                        <span>متبقي {s.remaining_seconds}ث</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white dark:bg-dark-deeper rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm p-5 sm:p-6">
        <h3 className="font-bold text-dark dark:text-gray-100 mb-6 flex items-center gap-2">
          <span>📊</span> مقارنة الإيرادات والمصاريف (آخر 3 أشهر)
        </h3>
        {loading ? <Skeleton className="h-64 w-full" /> : (
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--text-gray, #64748B)', fontFamily: 'Tajawal' }} axisLine={false} tickLine={false} dy={10} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text-gray, #64748B)' }} tickFormatter={v => (v / 1000).toFixed(0) + 'k'} axisLine={false} tickLine={false} dx={-10} />
                <Tooltip
                  formatter={v => formatIQD(v)}
                  contentStyle={{ backgroundColor: 'var(--bg-flat-white, white)', borderRadius: '12px', border: '1px solid var(--border-light, #E2E8F0)', color: 'var(--text-dark-h, #0F1E33)', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', fontFamily: 'Tajawal', fontSize: '12px' }}
                  itemStyle={{ color: 'var(--text-dark-s, #374151)' }}
                  cursor={{ fill: 'var(--bg-flat-50, #F8FAFC)' }}
                />
                <Legend iconType="circle" wrapperStyle={{ color: 'var(--text-dark-h, #0F1E33)', fontSize: '12px', paddingTop: '20px' }} />
                <Bar dataKey="إيرادات" fill="#17A589" radius={[4, 4, 0, 0]} maxBarSize={40} />
                <Bar dataKey="مصاريف" fill="#E74C3C" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
